import "./App.css";
import {useAuth} from "react-oidc-context";
import {useEffect, useMemo, useState} from "react";
import {useSelector, useDispatch} from "react-redux";
import {useTheme} from "@mui/material/styles";
import {styled} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Switch from "@mui/material/Switch";

import SvgIcon from "@mui/material/SvgIcon";

import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Tooltip from "@mui/material/Tooltip";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";

// Icons
import IconButton from "@mui/material/IconButton";
import BusinessIcon from "@mui/icons-material/Business";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LogoutIcon from "@mui/icons-material/Logout";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import SolarPowerIcon from "@mui/icons-material/SolarPower";
import BuildIcon from "@mui/icons-material/Build";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";

import {isAuthorized} from "./service/KeycloakService";
import {signinRedirectUri} from "./service/oidcCallbackUrl";
import {NOTIFICATION_ROLE} from "./app/platform/notifications/notificationRole";
import PersonIcon from "@mui/icons-material/Person";
import AppSettingsAltIcon from "@mui/icons-material/AppSettingsAlt";
import DescriptionIcon from "@mui/icons-material/Description";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import AppsIcon from "@mui/icons-material/Apps";
import CasesOutlinedIcon from "@mui/icons-material/CasesOutlined";

import logo from "./assets/logo/okwind_logo_small.png";

import AppVersion from "./AppVersion";
import {AppBar, Drawer, DrawerHeader} from "./Drawer";
import {NavLink, useLocation} from "react-router";
import {RootState} from "./store/store";
import {toggleTheme} from "./store/theme/theme.store";
import React from "react";
import {Collapse} from "@mui/material";
import OkwindIcon from "./OkwindIcon";
import {useTranslation} from "react-i18next";
import {roleLabel} from "./i18n/roles";

interface UserInfo {
  name: string;
  email: string;
}

interface MenuItemDef {
  label: string;
  icon: React.ReactElement;
  url: string;
  subMenuItem: MenuItemDef[];
}

interface MenuItemProps {
  menuItemDef: MenuItemDef;
  isMenuOpened: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  collapsable?: boolean;
  navigateTo?: string;
}

interface MenuItemGraph {
  itemListSizea: MenuItemDef[];
  itemListResource: MenuItemDef[];
  itemListFleetManagement: MenuItemDef[];
  itemListPlatformAdministration: MenuItemDef[];
}

interface MenuItemBlockProps {
  itemList: MenuItemDef[];
  isMenuOpened: boolean;
  handleDrawerOpen: () => void;
}

interface SecondMenuItemBlockProps {
  menuItem: MenuItemDef;
  isMenuOpened: boolean;
  handleDrawerOpen: () => void;
}

const MaterialUISwitch = styled(Switch)(({theme}) => ({
  width: 62,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff",
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: theme.palette.mode === "dark" ? "#003892" : "#001e3c",
    width: 32,
    height: 32,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        "#fff",
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
    borderRadius: 20 / 2,
  },
}));

function SecondMenuItemBlock(props: SecondMenuItemBlockProps) {
  const [openCollapse, setOpenCollapse] = React.useState(true);

  function handleOpenCollapse() {
    if (!props.isMenuOpened) props.handleDrawerOpen();
    setOpenCollapse(!openCollapse);
  }

  useEffect(() => {
    setOpenCollapse(props.isMenuOpened);
  }, [props.isMenuOpened]);

  return (
    <div>
      {props.menuItem.subMenuItem.length === 0 && (
        <AppMenuItem menuItemDef={props.menuItem} isMenuOpened={props.isMenuOpened} navigateTo={props.menuItem.url} />
      )}

      {props.menuItem.subMenuItem.length > 0 && (
        <>
          <AppMenuItem
            menuItemDef={props.menuItem}
            isMenuOpened={props.isMenuOpened}
            onClick={handleOpenCollapse}
            collapsable={openCollapse}
          />
          <Collapse in={openCollapse} timeout="auto" unmountOnExit>
            <List
              style={{
                marginLeft: "15px",
                borderLeft: "1px solid #35ba0746",
                paddingLeft: "10px",
                marginBottom: "4px",
              }}>
              {props.menuItem.subMenuItem.map((thirdLvlItem: MenuItemDef, index3: number) => (
                <AppMenuItem
                  key={index3}
                  menuItemDef={thirdLvlItem}
                  isMenuOpened={props.isMenuOpened}
                  navigateTo={thirdLvlItem.url}
                />
              ))}
            </List>
          </Collapse>
        </>
      )}
    </div>
  );
}

function MenuItemBlock(props: MenuItemBlockProps) {
  const theme = useTheme();

  return (
    <>
      {props.itemList.map((firstLvlItem: MenuItemDef, index1: number) => {
        return (
          <div key={index1}>
            {firstLvlItem.subMenuItem.length > 0 && (
              <Typography
                style={{
                  marginLeft: 20,
                  marginTop: -5,
                  marginBottom: 0,
                  color: theme.palette.mode === "dark" ? theme.palette.common.white : "rgb(18, 25, 38)",
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  display: props.isMenuOpened ? "block" : "none",
                }}
                variant="caption"
                gutterBottom={true}>
                {firstLvlItem.label}
              </Typography>
            )}

            {firstLvlItem.subMenuItem.length === 0 && (
              <AppMenuItem menuItemDef={firstLvlItem} isMenuOpened={props.isMenuOpened} navigateTo={firstLvlItem.url} />
            )}
            <List style={{marginLeft: "15px"}}>
              {firstLvlItem.subMenuItem.map((secondLvlItem: MenuItemDef, index2: number) => (
                <SecondMenuItemBlock
                  key={index2}
                  menuItem={secondLvlItem}
                  isMenuOpened={props.isMenuOpened}
                  handleDrawerOpen={props.handleDrawerOpen}
                />
              ))}
            </List>
          </div>
        );
      })}
    </>
  );
}

function BulletIcon() {
  const theme = useTheme();
  return (
    <SvgIcon>
      <svg
        width="140px"
        height="140px"
        style={{fill: theme.palette.mode === "dark" ? "white" : "black"}}
        viewBox="0 0 20 20"
        xmlns="http://www.w3.org/2000/svg">
        <path d="M12 10a2 2 0 1 0 0 4 2 2 0 1 0 0-4z" />
        <path fill="none" d="M0 0h24v24H0z" />
      </svg>
    </SvgIcon>
  );
}

function AppMenuItemButton(props: MenuItemProps) {
  const {pathname} = useLocation();
  const itemUrl = props.menuItemDef.url;
  const isSelected = itemUrl !== "" && (pathname === itemUrl || pathname.startsWith(`${itemUrl}/`));

  return (
    <ListItemButton
      onClick={props.onClick}
      style={{borderRadius: "8px", marginBottom: "1px"}}
      selected={isSelected}
      sx={{
        minHeight: 28,
        justifyContent: props.isMenuOpened ? "initial" : "center",
        px: 2.5,
        py: 0.25,
      }}>
      <ListItemIcon
        style={{
          marginRight: props.isMenuOpened ? 10 : 24,
          marginLeft: props.isMenuOpened ? -10 : 20,
        }}
        sx={{
          minWidth: 0,
          mr: props.isMenuOpened ? 3 : "auto",
          justifyContent: "center",
        }}>
        {props.menuItemDef.icon}
      </ListItemIcon>
      <ListItemText primary={props.menuItemDef.label} sx={{opacity: props.isMenuOpened ? 1 : 0}} />
      {props.menuItemDef.subMenuItem.length > 0 && props.isMenuOpened && (
        <>{props.collapsable ? <ExpandLessIcon /> : <ExpandMoreIcon />}</>
      )}
    </ListItemButton>
  );
}

function AppMenuItem(props: MenuItemProps) {
  return (
    <ListItem key={props.menuItemDef.label} title={props.menuItemDef.label} disablePadding sx={{display: "block"}}>
      {/* Pas de `<Tooltip>` MUI ici : le `title` du `ListItem` ci-dessus donne déjà le libellé au
          survol, sur les quatorze entrées et sans dépendre de MUI. Le tooltip MUI ne faisait que
          le redire, et seulement sur les douze entrées où il fonctionnait. */}
      {props.navigateTo && props.navigateTo !== "" ? (
        <NavLink to={props.navigateTo} style={{textDecoration: "none", color: "inherit"}}>
          <AppMenuItemButton menuItemDef={props.menuItemDef} isMenuOpened={props.isMenuOpened} />
        </NavLink>
      ) : (
        <AppMenuItemButton
          menuItemDef={props.menuItemDef}
          isMenuOpened={props.isMenuOpened}
          onClick={props.onClick}
          collapsable={props.collapsable}
        />
      )}
    </ListItem>
  );
}

function AppMenu() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const authorization = useSelector((state: RootState) => state.authorization.data);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>();
  const [menuItemGraph, setMenuItemGraph] = useState<MenuItemGraph>({
    itemListSizea: [],
    itemListResource: [],
    itemListFleetManagement: [],
    itemListPlatformAdministration: [],
  });
  const auth = useAuth();
  const {t} = useTranslation();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    // La redirection quitte la page : react-oidc-context porte l'éventuel échec dans `auth.error`.
    void auth.signoutRedirect();
  };

  useEffect(() => {
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;

    const handler = (error: Error) => {
      console.error("Silent renew error:", error);
      if (error.message?.includes("NetworkError")) {
        console.warn("Erreur réseau pendant le refresh, retry dans 5s...");
        // Un nouvel échec du renouvellement rappelle ce même handler : rien à intercepter ici.
        retryTimeout = setTimeout(() => void auth.signinSilent(), 5000);
      } else {
        console.error("Erreur de refresh, redirection login");
        // Retour sur la page où travaillait l'utilisateur, et non sur sa page d'arrivée.
        // La redirection quitte la page : react-oidc-context porte l'éventuel échec dans `auth.error`.
        void auth.signinRedirect({redirect_uri: signinRedirectUri(window.location.href)});
      }
    };

    auth.events.addSilentRenewError(handler);

    return () => {
      auth.events.removeSilentRenewError(handler);
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [auth]);

  useEffect(() => {
    if (authorization !== null && auth.user) {
      setUserInfo({
        email: auth.user.profile?.email ?? "",
        name: auth.user.profile?.name ?? "",
      });

      const itemListSizea: MenuItemDef[] = [
        {
          label: t("menu.presale"),
          icon: <ViewInArIcon />,
          url: "",
          subMenuItem: [],
        },
      ];
      if (authorization.authAppEconomicStudy) {
        itemListSizea[0].subMenuItem.push({
          label: t("menu.studies"),
          icon: <CasesOutlinedIcon />,
          url: "/studies/list",
          subMenuItem: [],
        });
      }

      const itemListFleetManagement: MenuItemDef[] = [
        {
          label: t("menu.fleetManagement"),
          icon: <BuildIcon />,
          url: "",
          subMenuItem: [],
        },
      ];

      if (authorization.authAppFleetManagement) {
        itemListFleetManagement[0].subMenuItem.push(
          {
            label: t("menu.clients"),
            icon: <PersonIcon />,
            url: "/fleet/client",
            subMenuItem: [],
          },
          {
            label: t("menu.plants"),
            icon: <SolarPowerIcon />,
            url: "/fleet/plant",
            subMenuItem: [],
          },
        );
      }

      if (authorization.authAppInstallationControl) {
        itemListFleetManagement[0].subMenuItem.push({
          label: t("menu.control"),
          icon: <ChecklistOutlinedIcon />,
          url: "",
          subMenuItem: [
            {
              label: t("menu.commissioning"),
              icon: <BulletIcon />,
              url: "/fleet/plants-to-control",
              subMenuItem: [],
            },
            {
              label: t("menu.checkpoints"),
              icon: <BulletIcon />,
              url: "/fleet/control",
              subMenuItem: [],
            },
          ],
        });
      }

      if (authorization.authAppScada) {
        itemListFleetManagement[0].subMenuItem.push({
          label: t("menu.scada"),
          icon: <QueryStatsIcon />,
          url: "",
          subMenuItem: [
            {
              label: t("menu.fleetView"),
              icon: <BulletIcon />,
              url: "/scada/plants",
              subMenuItem: [],
            },
            {
              label: t("menu.plantView"),
              icon: <BulletIcon />,
              url: "/scada/plant",
              subMenuItem: [],
            },
            {
              label: t("menu.events"),
              icon: <BulletIcon />,
              url: "/scada/alarms",
              subMenuItem: [],
            },
          ],
        });
      }

      const itemListResource: MenuItemDef[] = [
        {
          label: t("menu.resources"),
          icon: <AppsIcon />,
          url: "",
          subMenuItem: [],
        },
      ];

      if (authorization.authAppSoftware) {
        itemListResource[0].subMenuItem.push({
          label: t("menu.software"),
          icon: <AppSettingsAltIcon />,
          url: "/resource/software",
          subMenuItem: [],
        });
      }
      if (authorization.authAppDocumentation) {
        itemListResource[0].subMenuItem.push({
          label: t("menu.documentation"),
          icon: <DescriptionIcon />,
          url: "/resource/documentations",
          subMenuItem: [],
        });
      }

      const itemListPlatformAdministration: MenuItemDef[] = [
        {
          label: t("menu.accountManagement"),
          icon: <BuildIcon />,
          url: "",
          subMenuItem: [],
        },
      ];

      if (authorization.authAppUserAdmin) {
        itemListPlatformAdministration[0].subMenuItem.push({
          label: t("menu.users"),
          icon: <SupervisorAccountIcon />,
          url: "/platform/user",
          subMenuItem: [],
        });
      }
      if (authorization.authAppParkAdmin) {
        itemListPlatformAdministration[0].subMenuItem.push({
          label: t("menu.agencies"),
          icon: <BusinessIcon />,
          url: "/platform/park",
          subMenuItem: [],
        });
      }
      // Activé par rôle Keycloak, utilisateur par utilisateur, et non par une
      // autorisation applicative comme les entrées ci-dessus.
      if (isAuthorized(NOTIFICATION_ROLE)) {
        itemListPlatformAdministration[0].subMenuItem.push({
          label: t("menu.notifications"),
          icon: <NotificationsActiveIcon />,
          url: "/platform/notification",
          subMenuItem: [],
        });
      }

      setMenuItemGraph({
        itemListSizea,
        itemListResource,
        itemListPlatformAdministration,
        itemListFleetManagement,
      });
    }
  }, [authorization, auth.user, t]);

  useMemo(() => {
    const open = localStorage.getItem("drawer-open") || "1";
    setIsMenuOpened(open === "1");
  }, []);

  const handleDrawerOpen = () => {
    localStorage.setItem("drawer-open", "1");
    setIsMenuOpened(true);
  };

  const handleDrawerClose = () => {
    localStorage.setItem("drawer-open", "0");
    setIsMenuOpened(false);
  };

  function contentDrawer() {
    return (
      <div>
        {/* Étude avante-vente */}
        {menuItemGraph.itemListSizea.length > 0 && menuItemGraph.itemListSizea[0].subMenuItem.length > 0 && (
          <>
            <List>
              {isMobile ? (
                <></>
              ) : (
                <IconButton
                  size="small"
                  color="primary"
                  style={{
                    position: "fixed",
                    background: theme.palette.mode === "dark" ? "rgb(18, 18, 18)" : theme.palette.common.white,
                    marginTop: "-15px",
                    marginLeft: isMenuOpened ? "250px" : "68px",
                  }}
                  aria-label={t("menu.openDrawer")}
                  onClick={isMenuOpened ? handleDrawerClose : handleDrawerOpen}
                  edge="start">
                  {isMenuOpened ? (
                    <ArrowBackIosIcon style={{paddingLeft: "4px"}} />
                  ) : (
                    <ArrowForwardIosIcon style={{paddingLeft: "4px"}} />
                  )}
                </IconButton>
              )}
              <div style={{marginBottom: "5px"}}></div>
              <MenuItemBlock
                itemList={menuItemGraph.itemListSizea}
                isMenuOpened={isMenuOpened}
                handleDrawerOpen={handleDrawerOpen}
              />
            </List>
            <Divider className="menu-divider" />
          </>
        )}
        {/* Gestion du parc */}
        {menuItemGraph.itemListFleetManagement.length > 0 &&
          menuItemGraph.itemListFleetManagement[0].subMenuItem.length > 0 && (
            <>
              <List>
                <MenuItemBlock
                  itemList={menuItemGraph.itemListFleetManagement}
                  isMenuOpened={isMenuOpened}
                  handleDrawerOpen={handleDrawerOpen}
                />
              </List>
              <Divider className="menu-divider" />
            </>
          )}
        {/* Ressources */}
        {menuItemGraph.itemListResource.length > 0 && menuItemGraph.itemListResource[0].subMenuItem.length > 0 && (
          <>
            <List>
              <MenuItemBlock
                itemList={menuItemGraph.itemListResource}
                isMenuOpened={isMenuOpened}
                handleDrawerOpen={handleDrawerOpen}
              />
            </List>
            <Divider className="menu-divider" />
          </>
        )}
        {/* Gestion des comptes */}
        {menuItemGraph.itemListPlatformAdministration.length > 0 &&
          menuItemGraph.itemListPlatformAdministration[0].subMenuItem.length > 0 && (
            <>
              <List>
                <MenuItemBlock
                  itemList={menuItemGraph.itemListPlatformAdministration}
                  isMenuOpened={isMenuOpened}
                  handleDrawerOpen={handleDrawerOpen}
                />
              </List>
            </>
          )}
        {/* Numéro de version de l'application */}
        {isMenuOpened && <AppVersion />}
      </div>
    );
  }

  return (
    <div>
      <AppBar
        position="fixed"
        open={isMenuOpened}
        style={{
          boxShadow: "none",
          width: isMobile ? "100%" : isMenuOpened ? "calc(100% - 254px)" : "100%",
        }}>
        <Toolbar
          style={{
            backgroundColor: theme.palette.mode === "dark" ? "#121212" : theme.palette.common.white,
          }}>
          {isMobile ? (
            <IconButton
              color="primary"
              style={{
                background: theme.palette.dialogCloseButtonBackground,
                marginLeft: "0px",
                marginTop: "10px",
              }}
              aria-label={t("menu.openDrawer")}
              onClick={isMenuOpened ? handleDrawerClose : handleDrawerOpen}
              edge="start"
              sx={{
                marginRight: 2,
              }}>
              <MenuIcon />
            </IconButton>
          ) : (
            <></>
          )}
          {!isMobile && !isMenuOpened ? (
            <NavLink to={"/"} style={{cursor: "pointer", marginRight: "10px"}}>
              <img
                style={{
                  cursor: "pointer",
                  width: "40px",
                  marginTop: "5px",
                  display: "block",
                }}
                src={logo}
                alt={t("menu.logoAlt")}
              />
            </NavLink>
          ) : (
            <></>
          )}
          <span
            style={{
              fontSize: "40px",
              color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black,
              marginLeft: isMobile ? "-5px" : isMenuOpened ? "-17px" : "15px",
            }}>
            |
          </span>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{flexGrow: 1}}
            style={{
              color: theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black,
              marginLeft: isMobile ? "10px" : isMenuOpened ? "21px" : "12px",
              marginTop: "5px",
            }}>
            {t("menu.appTitle")}
          </Typography>

          <Box sx={{flexGrow: 0}}>
            <Tooltip title={t("menu.settings")} data-testid="settings">
              <IconButton onClick={handleOpenUserMenu} sx={{p: 0}}>
                <Avatar alt={userInfo?.name}>{userInfo?.name?.charAt(0)}</Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{mt: "45px"}}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}>
              <ListItem>
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                    }}>
                    {userInfo?.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                    }}>
                    {authorization?.role ? roleLabel(authorization.role) : null}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                    }}>
                    {userInfo?.email}
                  </Typography>
                </Box>
              </ListItem>
              <Divider></Divider>
              <ListItem>
                {t("menu.theme", {mode: t(theme.palette.mode === "dark" ? "menu.themeDark" : "menu.themeLight")})}&nbsp;
                <MaterialUISwitch
                  id="theme-toggle"
                  sx={{m: 1}}
                  onClick={() => {
                    dispatch(toggleTheme());
                  }}
                  checked={theme.palette.mode === "dark"}
                  data-testid="modeToogle"
                />
              </ListItem>
              <Divider style={{marginBottom: 5}}></Divider>
              <ListItem onClick={handleLogout} style={{cursor: "pointer"}} data-testid="logOutButton">
                <ListItemText>{t("menu.logout")}</ListItemText>
                <ListItemIcon style={{marginLeft: "-50px!important"}}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
              </ListItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <SwipeableDrawer
          anchor="left"
          style={{zIndex: theme.zIndex.appBar + 1000, boxShadow: "none"}}
          open={isMenuOpened}
          onClose={handleDrawerClose}
          onOpen={handleDrawerOpen}>
          <DrawerHeader style={{paddingTop: "20px"}}>
            <NavLink to={"/"} style={{cursor: "pointer", marginRight: "25px"}}>
              <OkwindIcon />
            </NavLink>
          </DrawerHeader>
          <div className="navbar-list">{contentDrawer()}</div>
        </SwipeableDrawer>
      ) : (
        <Drawer
          variant="permanent"
          open={isMenuOpened}
          style={{borderRight: "0px"}}
          sx={{
            "& .MuiPaper-root": {
              border: "none",
            },
          }}>
          <div className="navbar">
            <DrawerHeader style={{paddingTop: "13px"}}>
              <NavLink to={"/"} style={{cursor: "pointer", marginRight: "10px"}}>
                <OkwindIcon />
              </NavLink>
            </DrawerHeader>

            <div className="navbar-list">{contentDrawer()}</div>
          </div>
        </Drawer>
      )}
    </div>
  );
}

export default AppMenu;
